import { useEffect } from "react";
import useFetch from "hooks/useFetch";
import mixpanel from "mixpanel-browser";
import { useSetRecoilState } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import { setAuthHeaders } from "api";
import { userAtom } from "recoil/userAtom";
import { useNavigate } from "react-router-dom";
import { sendMessage } from "utils/webview";
import useIsInappWebview from "./useIsInappWebview";
import useGetDeviceInfo from "./useGetDeviceInfo";
import { compareVersions } from "../logic/logics_common";
import { loginBlockModalAtom } from "recoil/loginBlockModalAtom";
import { v4 as uuidv4 } from "uuid";

export default function useLogin({
  url,
  idToken,
  type,
}: {
  url: string;
  idToken: string;
  type: "google" | "apple";
}) {
  let navigate = useNavigate();

  const [loginResult, login] = useFetch({
    url: url,
    requestBody: JSON.stringify({
      idToken: idToken,
      type: type,
    }),
  });

  const setPatientState = useSetRecoilState(patientAtom);
  const setUser = useSetRecoilState(userAtom);
  const setLoginBlockModal = useSetRecoilState(loginBlockModalAtom);
  const isInappWebview = useIsInappWebview();
  const { appVersion } = useGetDeviceInfo();

  useEffect(() => {
    if (loginResult) {
      const result = loginResult.result;

      if (isInappWebview && result.role !== "patient") {
        setLoginBlockModal({
          open: true,
          title: "로그인 실패",
          subtitle: "코치는 PC에서 로그인해주세요.",
        });
        return;
      }

      mixpanel.identify(result.trackId);

      setAuthHeaders(result.accessToken);
      sendMessage(
        JSON.stringify({
          event: "value",
          params: {
            accessToken: result.accessToken,
          },
        })
      );

      setUser(result);
      if (result.role === "patient" && result.userId) {
        setPatientState({
          patientId: Number(`${result.userId}`),
          nickname: result.nickname,
        });
      }

      if (isInappWebview && compareVersions(appVersion, "1.1.20") >= 0) {
        if (result.isFirstLogin) {
          if (compareVersions(appVersion, "1.1.51") >= 0) {
            sendMessage(
              JSON.stringify({
                event: "send_meta_capi_event",
                params: {
                  eventId: uuidv4(),
                  eventName: "Login",
                },
              })
            );
          } else {
            sendMessage(
              JSON.stringify({
                event: "send_fb_event",
                params: {
                  eventName: "Login",
                },
              })
            );
          }
        }
      }

      if (isInappWebview && compareVersions(appVersion, "1.1.47") >= 0) {
        if (result.isFirstLogin) {
          sendMessage(
            JSON.stringify({
              event: "send_firebase_analytics_event",
              params: {
                eventName: "sign_up",
                eventParams: {
                  method: type,
                },
              },
            })
          );

          sendMessage(
            JSON.stringify({
              event: "send_appsflyer_event",
              params: {
                eventName: "af_complete_registration",
                eventParams: {
                  af_registration_method:
                    type.charAt(0).toUpperCase() + type.slice(1), // google -> Google
                },
              },
            })
          );
        }
      }

      navigate("/", { replace: true });
    }
  }, [loginResult]);

  return login;
}
